import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Col,
  ColorPicker,
  Flex,
  Form,
  Input,
  Modal,
  Popconfirm,
  Radio,
  Row,
  Select,
  Space,
  Switch,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import Table from 'components/Common/Table';
import {
  CodeOutlined,
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  KeyOutlined,
  MinusCircleOutlined,
  PlayCircleOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { BsChatDots } from "react-icons/bs";

import { useNotification } from "NotificationContext";
import * as templatesApi from "api/activityTemplates";
import * as api from "api/chatbot";
import { wsBase } from "realtime/useCommunicationSocket";

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

/**
 * Website chat widgets, one or more per product. Each row owns a public key
 * that the embed script on the product's site sends with every request; the
 * backend checks the request's Origin against `allowed_domains`.
 */

const DEFAULT_COLOR = "#7c4dff";

const DEFAULTS = {
  name: "",
  product: undefined,
  allowed_domains: [],
  greeting: "Hi there! How can we help you today?",
  offline_message: "We're offline right now. Leave a message and we'll get back to you.",
  primary_color: DEFAULT_COLOR,
  position: "right",
  launcher_text: "Chat with us",
  pre_chat_fields: ["name", "email"],
  require_pre_chat: false,
  quick_replies: [],
  quick_messages: [],
  kb_suggestions_enabled: true,
  auto_assign_to: null,
  is_active: true,
};

const PRE_CHAT_OPTIONS = [
  { label: "Name", value: "name" },
  { label: "Email", value: "email" },
  { label: "Phone", value: "phone" },
];

const QUICK_REPLY_ACTIONS = [
  { label: "Send as message", value: "message" },
  { label: "Hand off to an agent", value: "handoff" },
  { label: "Open a ticket", value: "ticket" },
  { label: "Search knowledge base", value: "kb" },
];

const errorText = (err) => {
  const data = err?.response?.data;
  if (!data) return "Something went wrong.";
  if (typeof data === "string") return data;
  if (data.error) return data.error;
  if (data.detail) return data.detail;
  return Object.entries(data)
    .map(([field, messages]) => `${field}: ${[].concat(messages).map(
      (item) => (typeof item === "object" ? JSON.stringify(item) : item)
    ).join(" ")}`)
    .join(" | ");
};

const activeTag = (value) =>
  value ? <Tag color="green">Active</Tag> : <Tag>Inactive</Tag>;

const maskKey = (key) => {
  if (!key) return "—";
  const tail = key.slice(-4);
  const head = key.startsWith("cb_pk_") ? "cb_pk_" : key.slice(0, 3);
  return `${head}••••••${tail}`;
};

const copyText = async (value) => {
  if (!value) return false;
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch (error) {
    // Older browsers / non-secure origins.
    const area = document.createElement("textarea");
    area.value = value;
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    let ok = false;
    try {
      ok = document.execCommand("copy");
    } catch (inner) {
      ok = false;
    }
    document.body.removeChild(area);
    return ok;
  }
};

const colorToHex = (value) => {
  if (!value) return DEFAULT_COLOR;
  if (typeof value === "string") return value;
  if (typeof value.toHexString === "function") return value.toHexString();
  return DEFAULT_COLOR;
};

/** A thumbnail of the product site with the launcher where visitors will see it. */
const LauncherPreview = ({ color, position, text }) => (
  <div
    style={{
      position: "relative",
      height: 150,
      borderRadius: 8,
      border: "1px solid #e8e8e8",
      background: "linear-gradient(#fafafa, #f0f2f5)",
      overflow: "hidden",
    }}
  >
    <div style={{ padding: 10 }}>
      <div style={{ height: 8, width: "60%", background: "#e4e6eb", borderRadius: 4 }} />
      <div
        style={{ height: 8, width: "40%", background: "#e4e6eb", borderRadius: 4, marginTop: 6 }}
      />
    </div>
    <div
      style={{
        position: "absolute",
        bottom: 12,
        [position === "left" ? "left" : "right"]: 12,
        display: "flex",
        alignItems: "center",
        gap: 6,
        background: color || DEFAULT_COLOR,
        color: "#fff",
        borderRadius: 22,
        padding: text ? "8px 14px" : 10,
        fontSize: 12.5,
        boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
      }}
    >
      <BsChatDots style={{ fontSize: 16 }} />
      {text}
    </div>
  </div>
);

const EmbedCodeModal = ({ chatbot, onClose }) => {
  const notify = useNotification();
  const snippet = api.embedSnippet(chatbot);
  const domains = chatbot?.allowed_domains || [];

  return (
    <Modal
      title={`Embed code · ${chatbot?.name || ""}`}
      open={!!chatbot}
      onCancel={onClose}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
        <Button
          key="copy"
          type="primary"
          icon={<CopyOutlined />}
          disabled={!snippet}
          onClick={async () => {
            const ok = await copyText(snippet);
            notify(ok ? "success" : "error", ok ? "Embed code copied" : "Could not copy");
          }}
        >
          Copy
        </Button>,
      ]}
      width={680}
    >
      {snippet ? (
        <pre
          style={{
            background: "#1e1e1e",
            color: "#d4d4d4",
            padding: 12,
            borderRadius: 6,
            whiteSpace: "pre-wrap",
            wordBreak: "break-all",
            fontSize: 12.5,
          }}
        >
          {snippet}
        </pre>
      ) : (
        <Alert type="warning" showIcon message="This chatbot has no public key yet." />
      )}
      <Paragraph style={{ marginTop: 12, marginBottom: 4 }} strong>
        Install
      </Paragraph>
      <ol style={{ paddingLeft: 20, marginBottom: 8 }}>
        <li>
          Paste the snippet on every page of the product site, just before the
          closing <Text code>{"</body>"}</Text> tag.
        </li>
        <li>
          Add the site's domain (for example <Text code>app.example.com</Text>) to
          this chatbot's <Text strong>Allowed domains</Text>, otherwise the widget
          will refuse to load there.
        </li>
        <li>Reload the site — the launcher appears in the configured corner.</li>
      </ol>
      {domains.length ? (
        <Text type="secondary">
          Currently allowed: {domains.map((domain) => <Tag key={domain}>{domain}</Tag>)}
        </Text>
      ) : (
        <Alert
          type="info"
          showIcon
          message="No allowed domains set — the widget will load on any site that has this key."
        />
      )}
    </Modal>
  );
};

// The widget is served by the same nginx as the API (/widget/chat.js on the
// API's host), so a test runs the real script against this backend.
const testWidgetUrls = () => {
  const api = String(process.env.REACT_APP_BASE_URL || "/api/").replace(/\/+$/, "");
  const origin = new URL(api, window.location.href).origin;
  return { script: `${origin}/widget/chat.js`, api, ws: wsBase() };
};

const TestChatbotModal = ({ chatbot, onClose }) => {
  // Bumped to remount the iframe, i.e. reload the widget.
  const [run, setRun] = useState(0);
  const domains = chatbot?.allowed_domains || [];
  const here = window.location.host;
  const blocked =
    domains.length > 0 &&
    !domains.some((domain) => {
      const entry = String(domain).toLowerCase().replace(/^https?:\/\//, "").split("/")[0];
      const host = here.toLowerCase();
      if (entry.startsWith("*.")) {
        const base = entry.slice(2);
        const name = host.split(":")[0];
        return name === base || name.endsWith(`.${base}`);
      }
      return entry.includes(":") ? entry === host : entry === host.split(":")[0];
    });

  const urls = testWidgetUrls();
  const srcDoc = chatbot?.public_key
    ? `<!doctype html><html><head><meta charset="utf-8"></head>
<body style="font-family:sans-serif;color:#888;padding:16px">Test page for ${chatbot.name
        .replace(/</g, "&lt;")}. Use the launcher in the corner.
<script src="${urls.script}" data-key="${chatbot.public_key}" data-api="${urls.api}" data-ws="${urls.ws}"></script>
</body></html>`
    : "";

  const restart = () => {
    // The widget remembers its session per key; drop it for a fresh chat.
    try {
      localStorage.removeItem(`teson_chat_${chatbot.public_key}`);
    } catch (e) {
      /* storage blocked */
    }
    setRun((value) => value + 1);
  };

  return (
    <Modal
      title={`Test chatbot · ${chatbot?.name || ""}`}
      open={!!chatbot}
      onCancel={onClose}
      width={520}
      destroyOnClose
      footer={[
        <Button key="restart" onClick={restart} disabled={!srcDoc}>
          Start new chat
        </Button>,
        <Button key="close" type="primary" onClick={onClose}>
          Close
        </Button>,
      ]}
    >
      {blocked && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={`Add "${here}" to Allowed domains to test from here — the widget refuses other sites.`}
        />
      )}
      {!chatbot?.is_active && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="This chatbot is inactive, so the widget will not load."
        />
      )}
      {srcDoc ? (
        <iframe
          key={run}
          title="Chatbot test"
          srcDoc={srcDoc}
          style={{ width: "100%", height: 560, border: "1px solid #eee", borderRadius: 8 }}
        />
      ) : (
        <Alert type="warning" showIcon message="This chatbot has no public key yet." />
      )}
      <Text type="secondary" style={{ display: "block", marginTop: 8 }}>
        Messages sent here arrive in the Communication Box like any visitor's.
      </Text>
    </Modal>
  );
};

const ChatbotConfigurationTab = () => {
  const notify = useNotification();
  const [products, setProducts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [embedFor, setEmbedFor] = useState(null);
  const [testFor, setTestFor] = useState(null);
  const [form] = Form.useForm();

  const previewColor = Form.useWatch("primary_color", form);
  const previewPosition = Form.useWatch("position", form);
  const previewText = Form.useWatch("launcher_text", form);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(api.rowsOf(await api.loadChatbots()));
    } catch (err) {
      if (err?.response?.status !== 404) {
        notify("error", "Could not load chatbots", errorText(err));
      }
      setRows([]);
    }
    setLoading(false);
  }, [notify]);

  useEffect(() => {
    load();
    templatesApi
      .loadProducts()
      .then((response) => setProducts(templatesApi.rowsOf(response)))
      .catch(() => setProducts([]));
    api
      .loadEmployees()
      .then((response) => setEmployees(api.rowsOf(response)))
      .catch(() => setEmployees([]));
  }, [load]);

  const openForm = (record) => {
    setEditing(record || null);
    setOpen(true);
    form.setFieldsValue(
      record
        ? {
          ...DEFAULTS,
          ...record,
          allowed_domains: record.allowed_domains || [],
          pre_chat_fields: record.pre_chat_fields || [],
          quick_replies: record.quick_replies || [],
          quick_messages: record.quick_messages || [],
        }
        : DEFAULTS
    );
  };

  const save = async (values) => {
    const payload = {
      ...values,
      primary_color: colorToHex(values.primary_color),
      allowed_domains: (values.allowed_domains || [])
        .map((domain) =>
          String(domain)
            .trim()
            .replace(/^https?:\/\//i, "")
            .replace(/\/.*$/, "")
            .toLowerCase()
        )
        .filter(Boolean),
      quick_replies: (values.quick_replies || [])
        .filter((reply) => reply && reply.label)
        .map((reply) => ({
          label: reply.label,
          action: reply.action || "message",
          payload: reply.payload || "",
        })),
      quick_messages: (values.quick_messages || [])
        .filter((item) => item && item.title && item.text)
        .map((item) => ({ title: item.title.trim(), text: item.text.trim() })),
      auto_assign_to: values.auto_assign_to || null,
    };
    setSaving(true);
    try {
      if (editing) await api.updateChatbot(editing.id, payload);
      else await api.createChatbot(payload);
      notify("success", `Chatbot ${editing ? "updated" : "created"}`);
      setOpen(false);
      form.resetFields();
      load();
    } catch (err) {
      notify("error", "Could not save chatbot", errorText(err));
    }
    setSaving(false);
  };

  const remove = async (id) => {
    try {
      await api.deleteChatbot(id);
      notify("success", "Chatbot deleted");
      load();
    } catch (err) {
      notify("error", "Could not delete chatbot", errorText(err));
    }
  };

  const regenerate = async (record) => {
    try {
      const response = await api.regenerateChatbotKey(record.id);
      notify("success", "New key generated", "Update the embed code on every site that uses this chatbot.");
      await load();
      if (response?.data?.public_key) {
        setEmbedFor({ ...record, ...response.data });
      }
    } catch (err) {
      notify("error", "Could not regenerate the key", errorText(err));
    }
  };

  const columns = [
    { title: "Name", dataIndex: "name" },
    { title: "Product", dataIndex: "product_name" },
    {
      title: "Allowed domains",
      dataIndex: "allowed_domains",
      render: (value) =>
        value && value.length ? (
          <Space size={[4, 4]} wrap>
            {value.map((domain) => (
              <Tag key={domain}>{domain}</Tag>
            ))}
          </Space>
        ) : (
          <Text type="secondary">Any</Text>
        ),
    },
    { title: "Status", dataIndex: "is_active", render: activeTag },
    {
      title: "Public key",
      dataIndex: "public_key",
      render: (value) => (
        <Space size={4}>
          <Text code style={{ fontSize: 12 }}>
            {maskKey(value)}
          </Text>
          {value ? (
            <Tooltip title="Copy key">
              <a
                onClick={async () => {
                  const ok = await copyText(value);
                  notify(ok ? "success" : "error", ok ? "Key copied" : "Could not copy");
                }}
              >
                <CopyOutlined />
              </a>
            </Tooltip>
          ) : null}
        </Space>
      ),
    },
    {
      title: "Actions",
      render: (_, record) => (
        <Flex gap="middle">
          <Tooltip title="Test chatbot">
            <a onClick={() => setTestFor(record)}>
              <PlayCircleOutlined />
            </a>
          </Tooltip>
          <Tooltip title="Embed code">
            <a onClick={() => setEmbedFor(record)}>
              <CodeOutlined />
            </a>
          </Tooltip>
          <Tooltip title="Edit">
            <a onClick={() => openForm(record)}>
              <EditOutlined />
            </a>
          </Tooltip>
          <Popconfirm
            title="Regenerate the public key?"
            description={
              <div style={{ maxWidth: 260 }}>
                Every site already embedding this chatbot stops working until its
                snippet is updated with the new key.
              </div>
            }
            okText="Regenerate"
            okButtonProps={{ danger: true }}
            onConfirm={() => regenerate(record)}
          >
            <Tooltip title="Regenerate key">
              <a>
                <KeyOutlined />
              </a>
            </Tooltip>
          </Popconfirm>
          <Popconfirm title="Delete this chatbot?" onConfirm={() => remove(record.id)}>
            <a>
              <DeleteOutlined />
            </a>
          </Popconfirm>
        </Flex>
      ),
    },
  ];

  return (
    <>
      <Flex justify="space-between" align="center" style={{ marginBottom: 12 }}>
        <Text type="secondary">
          A chat widget for a product's website. Chats land in the Communication inbox.
        </Text>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openForm(null)}>
          Add Chatbot
        </Button>
      </Flex>
      <Table
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={rows}
        columns={columns}
        pagination={{ defaultPageSize: 20, showSizeChanger: true }}
      />

      <Modal
        title={editing ? "Edit Chatbot" : "Add Chatbot"}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={saving}
        width={860}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={save} preserve={false}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Name" name="name" rules={[{ required: true }]}>
                <Input placeholder="Clockwise website chat" />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Product" name="product" rules={[{ required: true }]}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={products.map((product) => ({
                    label: product.name,
                    value: product.id,
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Allowed domains"
            name="allowed_domains"
            extra="Host names the widget may load on, e.g. clockwise.teson.in, *.example.com or localhost:3000. Leave empty to allow any."
          >
            <Select mode="tags" tokenSeparators={[",", " "]} placeholder="Type a domain and press Enter" />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Greeting" name="greeting">
                <TextArea autoSize={{ minRows: 2, maxRows: 4 }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Offline message" name="offline_message">
                <TextArea autoSize={{ minRows: 2, maxRows: 4 }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={14}>
              <Row gutter={12}>
                <Col span={12}>
                  <Form.Item
                    label="Primary colour"
                    name="primary_color"
                    getValueFromEvent={(color) => colorToHex(color)}
                  >
                    <ColorPicker format="hex" showText disabledAlpha />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label="Position" name="position">
                    <Radio.Group
                      optionType="button"
                      options={[
                        { label: "Left", value: "left" },
                        { label: "Right", value: "right" },
                      ]}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item label="Launcher text" name="launcher_text">
                <Input placeholder="Chat with us" maxLength={40} />
              </Form.Item>
            </Col>
            <Col xs={24} md={10}>
              <Form.Item label="Preview">
                <LauncherPreview
                  color={colorToHex(previewColor)}
                  position={previewPosition}
                  text={previewText}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16} align="middle">
            <Col xs={24} md={14}>
              <Form.Item label="Pre-chat form fields" name="pre_chat_fields">
                <Checkbox.Group options={PRE_CHAT_OPTIONS} />
              </Form.Item>
            </Col>
            <Col xs={24} md={10}>
              <Form.Item
                label="Require pre-chat form"
                name="require_pre_chat"
                valuePropName="checked"
                tooltip="When on, visitors must fill the form before they can type."
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Quick replies" style={{ marginBottom: 8 }}>
            <Form.List name="quick_replies">
              {(fields, { add, remove: removeReply }) => (
                <>
                  {fields.map(({ key, name, ...rest }) => (
                    <Row key={key} gutter={8} align="top">
                      <Col xs={24} md={7}>
                        <Form.Item
                          {...rest}
                          name={[name, "label"]}
                          rules={[{ required: true, message: "Label" }]}
                        >
                          <Input placeholder="Button label" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={7}>
                        <Form.Item {...rest} name={[name, "action"]} initialValue="message">
                          <Select options={QUICK_REPLY_ACTIONS} />
                        </Form.Item>
                      </Col>
                      <Col xs={22} md={9}>
                        <Form.Item {...rest} name={[name, "payload"]}>
                          <Input placeholder="Payload (message text, KB query…)" />
                        </Form.Item>
                      </Col>
                      <Col xs={2} md={1} style={{ paddingTop: 6 }}>
                        <MinusCircleOutlined onClick={() => removeReply(name)} />
                      </Col>
                    </Row>
                  ))}
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    disabled={fields.length >= 10}
                    onClick={() => add({ label: "", action: "message", payload: "" })}
                  >
                    Add quick reply
                  </Button>
                </>
              )}
            </Form.List>
          </Form.Item>

          <Form.Item
            label="Quick messages"
            extra="Saved replies your team can insert while chatting (⚡ in the chat box, or type / ). Visitors never see this list."
            style={{ marginBottom: 8 }}
          >
            <Form.List name="quick_messages">
              {(fields, { add, remove: removeMessage }) => (
                <>
                  {fields.map(({ key, name, ...rest }) => (
                    <Row key={key} gutter={8} align="top">
                      <Col xs={24} md={7}>
                        <Form.Item
                          {...rest}
                          name={[name, "title"]}
                          rules={[{ required: true, whitespace: true, message: "Title" }]}
                        >
                          <Input placeholder="Title, e.g. Pricing" maxLength={80} />
                        </Form.Item>
                      </Col>
                      <Col xs={22} md={16}>
                        <Form.Item
                          {...rest}
                          name={[name, "text"]}
                          rules={[{ required: true, whitespace: true, message: "Message" }]}
                        >
                          <TextArea
                            placeholder="Message text"
                            autoSize={{ minRows: 1, maxRows: 4 }}
                            maxLength={2000}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={2} md={1} style={{ paddingTop: 6 }}>
                        <MinusCircleOutlined onClick={() => removeMessage(name)} />
                      </Col>
                    </Row>
                  ))}
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    disabled={fields.length >= 50}
                    onClick={() => add({ title: "", text: "" })}
                  >
                    Add quick message
                  </Button>
                </>
              )}
            </Form.List>
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Auto-assign new chats to"
                name="auto_assign_to"
              >
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="Nobody (leave unassigned)"
                  options={[
                    ...(editing?.auto_assign_to
                      && !employees.some((row) => row.id === editing.auto_assign_to)
                      ? [{
                        value: editing.auto_assign_to,
                        label: editing.auto_assign_to_name || "Current assignee",
                      }]
                      : []),
                    ...employees.map((row) => ({
                      value: row.id,
                      label: api.employeeLabel(row),
                    })),
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item
                label="KB suggestions"
                name="kb_suggestions_enabled"
                valuePropName="checked"
                tooltip="Suggest knowledge-base articles while the visitor types."
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item label="Active" name="is_active" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <EmbedCodeModal chatbot={embedFor} onClose={() => setEmbedFor(null)} />
      <TestChatbotModal chatbot={testFor} onClose={() => setTestFor(null)} />
    </>
  );
};

export default ChatbotConfigurationTab;
